/** Dependencies */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Styles */
import {
  Box,
  TableContainer,
  TableSortLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
} from '@mui/material';

/** Components */
import LoadingState from '../../../../components/LoadingState';
import FormSelectSearchable from '../../../../components/FormSelectSearchable';

/** Hooks */
import useIntegrations from '../../../../hooks/useIntegrations';
import getActiveLanguage from '../../../../helpers/getActiveLanguage';
import usePropertyGroups from '../../../../hooks/usePropertyGroups';
import SearchBar from '../../../../components/SearchBar';

function PropertiesForm() {
  const { t } = useTranslation('integrations');
  const {
    fetchPropertySettings,
    settingsPropertyList,
    matchIntegrationProperty,
    unmatchIntegrationProperty,
    metaPropertyList,
    loading,
  } = useIntegrations();
  const { fetchPropertyGroups, propertyGroups } = usePropertyGroups();

  const [sort, setSort] = useState('');
  const [isSorted, setIsSorted] = useState({
    name: '',
    price: '',
  });
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState<{
    inputValue: string;
    isActive: boolean | null;
  }>({
    inputValue: '',
    isActive: null,
  });
  const [debouncedSearch, setDebouncedSearch] = useState({
    inputValue: '',
    isActive: true,
  });
  const formattedProperties = propertyGroups.map(item => {
    const itemsArray = item.items.map(item2 => ({
      id: item2.id,
      name: item2.translations,
      category_name: item.translations,
    }));

    return {
      items: itemsArray,
    };
  });

  const debouncedAndFilteredSearch = async () => {
    try {
      fetchPropertySettings('trendyol', 1, searchValue.inputValue, sort);
      // eslint-disable-next-line no-empty
    } catch (err) {}
  };

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    if (value !== page) {
      setPage(value);
      fetchPropertySettings('trendyol', value, searchValue.inputValue, sort);
    }
  };

  const handleClick = async (rowId: string, productId: string) => {
    if (productId) {
      await matchIntegrationProperty('trendyol', rowId, productId);
    } else {
      await unmatchIntegrationProperty('trendyol', rowId);
    }

    fetchPropertySettings('trendyol', page, searchValue.inputValue, sort);
  };

  const handleSort = async (type: string, sortType: string) => {
    let sortValue = '';

    if (type === 'name') {
      setIsSorted({
        ...isSorted,
        name: sortType,
      });
      sortValue = `${sortType},${isSorted.price}`;
    } else {
      setIsSorted({
        ...isSorted,
        price: sortType,
      });
      sortValue = `${isSorted.name},${sortType}`;
    }

    sortValue = sortValue.replace(/^,+|,+$/g, '');
    sortValue = sortValue.replace(/,+/g, ',');

    setSort(sortValue);

    fetchPropertySettings('trendyol', page, searchValue.inputValue, sortValue);
  };

  useEffect(() => {
    fetchPropertyGroups();
  }, []);

  useEffect(() => {
    debouncedAndFilteredSearch();
  }, [debouncedSearch.inputValue]);

  if (loading) {
    return <LoadingState />;
  }

  return (
    <Box sx={{ py: 3, px: 1 }}>
      <SearchBar
        placeholder={t('placeholder')}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        setDebouncedSearch={setDebouncedSearch}
      />
      <TableContainer sx={{ py: 1 }}>
        <Table sx={{ minWidth: 1280 }} aria-label="simple table">
          <TableHead sx={{ backgroundColor: 'primary.light' }}>
            <TableRow>
              <TableCell
                onClick={() => {
                  handleSort(
                    'name',
                    isSorted.name === ''
                      ? 'name'
                      : isSorted.name === 'name'
                      ? '-name'
                      : '',
                  );
                }}
              >
                <TableSortLabel
                  active={isSorted.name.length > 0}
                  direction={isSorted.name === 'name' ? 'asc' : 'desc'}
                >
                  {t('tables.propertyTable.integration_property_name')}
                </TableSortLabel>
              </TableCell>
              <TableCell
                onClick={() => {
                  handleSort(
                    'price',
                    isSorted.price === ''
                      ? 'price'
                      : isSorted.price === 'price'
                      ? '-price'
                      : '',
                  );
                }}
              >
                <TableSortLabel
                  active={isSorted.price.length > 0}
                  direction={isSorted.price === 'price' ? 'asc' : 'desc'}
                >
                  {t('tables.propertyTable.amount')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                {t('tables.propertyTable.kitchrest_property_name')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settingsPropertyList?.map(tableRow => (
              <TableRow>
                <TableCell>{tableRow.name}</TableCell>
                <TableCell>{tableRow?.price?.formatted || '₺0,00'}</TableCell>
                <TableCell>
                  <FormSelectSearchable
                    label=""
                    onChange={e => handleClick(tableRow.id, e)}
                    value={tableRow?.property_group_item?.id || ''}
                    options={formattedProperties
                      .flatMap(obj => obj.items)
                      .map(p => ({
                        id: p.id,
                        text: getActiveLanguage(p.name).name,
                        group: getActiveLanguage(p.category_name).name,
                      }))
                      .sort((a, b) => a.group.localeCompare(b.group))}
                    groupBy={option => option.group || ''}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination
          sx={{ margin: '10px' }}
          // @ts-ignore
          count={Math.ceil(
            (metaPropertyList?.total || 1) / (metaPropertyList?.per_page || 1),
          )}
          page={page}
          onChange={handleChange}
        />
      </TableContainer>
    </Box>
  );
}

export default PropertiesForm;
